import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Filter } from 'lucide-react';
import { getExperiences, resolveImg } from '../lib/api';
import { useSettings } from '../lib/SettingsContext';

import { useLang } from '../lib/i18n';
const formatLong = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
const formatChip = (iso) => {
  const d = new Date(iso);
  return { day: d.getDate().toString().padStart(2, '0'), month: d.toLocaleString('es-ES', { month: 'short' }).replace('.', '').toUpperCase() };
};

export default function Experiencias() {
  const { ui } = useLang();
  const { settings } = useSettings();
  const [experiences, setExperiences] = useState([]);
  const [filter, setFilter] = useState('TODOS');

  useEffect(() => { getExperiences().then(setExperiences).catch(() => {}); }, []);

  const months = useMemo(() => ['TODOS', ...Array.from(new Set(experiences.map((e) => e.month)))], [experiences]);
  const grouped = useMemo(() => {
    const filtered = filter === 'TODOS' ? experiences : experiences.filter((e) => e.month === filter);
    return filtered.reduce((acc, exp) => {
      acc[exp.month] = acc[exp.month] || [];
      acc[exp.month].push(exp);
      return acc;
    }, {});
  }, [filter, experiences]);
  const heroImg = resolveImg(settings.hero_images?.[0] || '');

  return (
    <div>
      <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 gd-hero-bg" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.55), rgba(13,13,14,0.95))' }} />
        <div className="relative z-10 text-center px-6">
          <div className="gd-eyebrow mb-3">Calendario 2026</div>
          <h1 className="font-script text-[#c9a063] text-5xl md:text-7xl leading-none">{ui('experiences.title')}</h1>
          <p className="font-serif-display text-white text-2xl md:text-3xl mt-4">{ui('experiences.sub')}</p>
        </div>
      </section>

      <section className="py-10 px-6 md:px-10 bg-[#0a0a0b] border-b border-white/5 sticky top-[72px] z-30 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center gap-3 overflow-x-auto">
          <Filter size={16} className="text-[#c9a063] flex-shrink-0" />
          {months.map((m) => (
            <button key={m} onClick={() => setFilter(m)} className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border whitespace-nowrap transition-colors ${filter === m ? 'bg-[#c9a063] text-[#0d0d0e] border-[#c9a063]' : 'border-white/15 text-white/70 hover:border-[#c9a063] hover:text-[#c9a063]'}`}>{m}</button>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 md:px-10 bg-[#0d0d0e]">
        <div className="max-w-6xl mx-auto space-y-16">
          {Object.entries(grouped).map(([month, items]) => (
            <div key={month}>
              <div className="flex items-end gap-4 mb-8">
                <div className="font-serif-display text-4xl md:text-6xl text-white/15 leading-none">{month}</div>
                <div className="flex-1 h-px bg-white/10 mb-3" />
                <div className="text-[#c9a063] gd-eyebrow mb-3">{items.length} experiencia{items.length > 1 ? 's' : ''}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {items.map((exp) => {
                  const chip = formatChip(exp.date);
                  return (
                    <Link to={`/experiencias/${exp.id}`} key={exp.id} className="gd-card group overflow-hidden">
                      <div className="relative aspect-[16/9] overflow-hidden bg-[#1a1a1d]">
                        {exp.cover && <img src={resolveImg(exp.cover)} alt={exp.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85))' }} />
                        <div className="absolute top-4 left-4 bg-[#0d0d0e]/85 backdrop-blur px-3 py-2 text-center border border-[#c9a063]/40">
                          <div className="font-serif-display text-2xl text-[#c9a063] leading-none">{chip.day}</div>
                          <div className="text-[10px] tracking-widest text-white/70 mt-1">{chip.month}</div>
                        </div>
                        {exp.status && exp.status !== 'open' && (
                          <div className={`absolute top-4 right-4 px-3 py-1 backdrop-blur text-[10px] tracking-widest uppercase ${exp.status === 'completo' ? 'bg-amber-600/90 text-white' : exp.status === 'celebrado' ? 'bg-blue-600/90 text-white' : 'bg-white/10 text-white/70'}`}>
                            {exp.status === 'completo' ? 'Completo' : exp.status === 'celebrado' ? 'Celebrado' : exp.status}
                          </div>
                        )}
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="text-[10px] tracking-[0.3em] text-[#c9a063] uppercase mb-1.5">{exp.region}</div>
                          <h3 className="font-serif-display text-xl md:text-2xl text-white leading-tight">{exp.title}</h3>
                        </div>
                      </div>
                      <div className="p-6 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-white/55 text-sm">
                          <Calendar size={14} className="text-[#c9a063]" /> <span className="capitalize">{formatLong(exp.date)}</span>
                        </div>
                        <span className="text-[11px] uppercase tracking-[0.25em] text-[#c9a063] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">{ui('btn.record')} <ArrowRight size={12} /></span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
